import { normalizeOneTrustPhone } from "./validity";

type OneTrustFieldValue = { isCheckbox: boolean; value: string };

// Populated from onBeforeSubmit (see submit-capture.ts), so we can build a clean form for the SDK
// from known-good values at submit time - by formsSubmitted, the form (and its values) are
// already gone.
export const oneTrustFieldValues = new Map<string, OneTrustFieldValue>();

// Set from onBeforeSubmit for the same reason - resolved server-side, see
// SalesforceConsentElementBlock.cshtml/OneTrustConsentService.ResolveAnalyticsCollectionPoint.
export let analyticsCollectionPoint = "UNKNOWN";

const PHONE_SYNC_TIMEOUT_MS = 2_000;
const PHONE_SYNC_POLL_MS = 25;

export type PhoneSyncResult = {
  // NotApplicable: no PhoneInput.razor phone field tagged for OneTrust in this form.
  // InSync: the tagged input already matched the visible one. Waited: it caught up within the
  // timeout. TimedOut: it didn't, so the payload may lack the number the visitor typed.
  status: "NotApplicable" | "InSync" | "Waited" | "TimedOut";
  waitedMs: number;
};

// Set from onBeforeSubmit by waitForPhoneInputSync, for telemetry (see describePayload).
export let phoneSync: PhoneSyncResult = { status: "NotApplicable", waitedMs: 0 };

// What PhoneInput.razor's PhoneValue setter makes the hidden bound input's value, given the
// visible input's. Once Blazor has re-rendered the component, the visible input can also show
// that bound value itself (OnParametersSetAsync sets Phone = Value), so that counts as synced too.
const isPhoneInputSynced = (
  visibleInput: HTMLInputElement,
  boundInput: HTMLInputElement
) => {
  const visible = visibleInput.value;
  const areaCode = visibleInput
    .closest(".c-phone-input")
    ?.querySelector<HTMLInputElement>(".c-phone-input__area")
    ?.placeholder.replace(/^\+/, "");

  let expected = visible;

  if (visible.trim() && areaCode) {
    expected = areaCode + (visible.startsWith("0") ? visible.substring(1) : visible);
  }

  return boundInput.value === expected || boundInput.value === visible;
};

// PhoneInput.razor's tagged input is updated by Blazor Server only after the visible input's
// change event has made a round trip to the server - and clicking submit straight after typing
// the number fires that change event just before onBeforeSubmit runs. Read straight away, the
// OneTrust payload would then lack the number (or carry a previous one), while Forms, which only
// reads the fields once onBeforeSubmit has resolved, posts the number to the CMS - i.e. the
// "CMS has email and phone, OneTrust only the email" mismatch. So wait (briefly) for it to catch up.
export const waitForPhoneInputSync = async (
  form: HTMLFormElement
): Promise<PhoneSyncResult> => {
  const boundInput = form.querySelector<HTMLInputElement>(
    '[data-ssocc-sdk-id="phoneId"]'
  );
  const visibleInput = boundInput
    ?.closest(".c-phone-input")
    ?.querySelector<HTMLInputElement>(".c-phone-input__phone");

  if (!boundInput || !visibleInput) {
    phoneSync = { status: "NotApplicable", waitedMs: 0 };
    return phoneSync;
  }

  if (isPhoneInputSynced(visibleInput, boundInput)) {
    phoneSync = { status: "InSync", waitedMs: 0 };
    return phoneSync;
  }

  const startedAt = performance.now();
  let status: PhoneSyncResult["status"] = "Waited";

  do {
    if (performance.now() - startedAt >= PHONE_SYNC_TIMEOUT_MS) {
      status = "TimedOut";
      break;
    }

    await new Promise(resolve => setTimeout(resolve, PHONE_SYNC_POLL_MS));
  } while (!isPhoneInputSynced(visibleInput, boundInput));

  phoneSync = { status, waitedMs: Math.round(performance.now() - startedAt) };
  return phoneSync;
};

// PhoneInput.razor's visible, user-typed input isn't the one carrying data-ssocc-sdk-id (that's
// on its hidden bound counterpart, which Blazor updates without dispatching a change event), so
// change events can't reliably drive this - read every tagged field's current value instead.
//
// Cleared before each capture so a field that's tagged in one submit attempt but no longer
// present in the DOM in a later one (e.g. a conditionally-rendered field, or a different form
// entirely) can't leave its previous value behind for buildConsentCaptureForm to still send.
export const captureOneTrustFieldValues = (form: HTMLFormElement): void => {
  oneTrustFieldValues.clear();

  form.querySelectorAll<HTMLInputElement>("[data-ssocc-sdk-id]").forEach(element => {
    const sdkId = element.dataset.ssoccSdkId;

    if (sdkId) {
      const isCheckbox = element.type === "checkbox";

      let value: string;

      if (isCheckbox) {
        value = String(element.checked);
      } else if (sdkId === "phoneId" && element.value) {
        // The OneTrust SDK expects phone numbers to be in E.164 format, which means they
        // should start with a '+' followed by the country code and the subscriber number.
        // Separators are stripped here only - see normalizeOneTrustPhone.
        value = `+${normalizeOneTrustPhone(element.value)}`;
      } else {
        // Trimmed like EPiServer Forms does before validating and posting a textbox's value -
        // otherwise e.g. the trailing space a mobile keyboard adds after an email suggestion is
        // sent to OneTrust as part of the email, even though Forms accepted (and stored) it.
        value = element.value.trim();
      }

      oneTrustFieldValues.set(sdkId, { isCheckbox, value });
    }
  });
};

export const captureAnalyticsCollectionPoint = (form: HTMLFormElement): void => {
  const marker = form.querySelector<HTMLElement>("[data-ssocc-touchpoint]");

  analyticsCollectionPoint = marker?.dataset.ssoccAnalyticsCollectionPoint || "UNKNOWN";
};

// Identifies exactly what buildConsentCaptureForm() + analyticsCollectionPoint would send, so an
// identical payload already captured successfully isn't sent to OneTrust a second time (see
// submitOneTrustConsentCapture). Sorted so field order in the DOM doesn't matter.
export const getConsentCaptureSignature = (): string =>
  JSON.stringify([
    analyticsCollectionPoint,
    Array.from(oneTrustFieldValues.entries()).sort(([a], [b]) => a.localeCompare(b))
  ]);

export const buildConsentCaptureForm = (): HTMLFormElement => {
  const form = document.createElement("form");

  oneTrustFieldValues.forEach(({ isCheckbox, value }, sdkId) => {
    const input = document.createElement("input");

    input.dataset.ssoccSdkId = sdkId;

    if (isCheckbox) {
      input.type = "checkbox";
      input.checked = value === "true";
    } else {
      input.type = "text";
      input.value = value;
    }

    form.append(input);
  });

  return form;
};
